/**
 * P3 손실 구조 분석 화면.
 *
 * paper 운영의 실현/미실현 손익 구조를 한 화면에서 본다.
 * 매번 SQL을 안 돌려도 LIVE 전환 판단·Layer 1 우선순위를 데이터로 설명 가능.
 */
import { useCallback, useEffect, useState } from 'react';
import LossKpiCards from '../components/loss/LossKpiCards';
import LossBreakdownTables from '../components/loss/LossBreakdownTables';
import LossLayer1Readiness from '../components/loss/LossLayer1Readiness';
import LossSignalDrift from '../components/loss/LossSignalDrift';
import LossSellAnalysis from '../components/loss/LossSellAnalysis';
import {
  fetchLossStructureSummary,
  LossStructureSummary,
} from '../services/lossStructureApi';
import './LossStructure.css';

export default function LossStructure() {
  const [data, setData] = useState<LossStructureSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await fetchLossStructureSummary());
    } catch (err) {
      setError(err instanceof Error ? err.message : '손실 구조 조회 실패');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && !data) {
    return <div className="loss-page"><div className="state-box">손실 구조 분석 로딩 중...</div></div>;
  }

  if (error && !data) {
    return (
      <div className="loss-page">
        <div className="state-box error-state">API 오류: {error}</div>
        <button className="loss-refresh" onClick={load}>다시 시도</button>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="loss-page">
      <header className="loss-header">
        <div>
          <h1>손실 구조 분석</h1>
          <p>실현 손익이 어디서 발생하는가? · 신호 강도·종목·보유 기간별 분해</p>
        </div>
        <button className="loss-refresh" onClick={load} disabled={loading}>
          {loading ? '갱신 중...' : '새로고침'}
        </button>
      </header>

      {error && (
        <div className="state-box error-state loss-banner">
          최신 조회 실패: {error} (이전 데이터 표시 중)
        </div>
      )}

      <LossKpiCards kpis={data.kpis} verification={data.verification} />
      <LossBreakdownTables
        byTradeType={data.by_trade_type}
        bySymbol={data.by_symbol}
        holdPeriod={data.hold_period}
      />
      <LossSellAnalysis analysis={data.sell_analysis} />
      <LossLayer1Readiness data={data.layer1_readiness} />
      <LossSignalDrift
        signal={data.signal_strength}
        drift={data.strategy_drift}
        open={data.open_positions}
      />
    </div>
  );
}
